import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { SelectOptionModel } from '../../../../shared/models/select-option.model';
import { SharedToasterService } from '../../../../shared/services/shared-toaster.service';
import { AssignmentScheduleFilterOptionModel } from '../../models/assignment-schedule-filter-option.model';
import { AssignmentScheduleFilterOptionsModel } from '../../models/assignment-schedule-filter-options.model';
import { AssignmentSchedulesFilterModel } from '../../models/assignment-schedules-filter.model';
import { AssignmentScheduleStateService } from '../../services/assignment-schedule-state.service';
import { AssignmentScheduleService } from '../../services/assignment-schedule.service';
import { AssignmentScheduleOptionAnswerEnum } from '../../enums/assignment-schedule-option-answer.enum';

@Component({
    templateUrl: './assignment-schedule-answer-filter-modal.component.html',
    styleUrl: './assignment-schedule-answer-filter-modal.component.scss'
})
export class AssignmentScheduleAnswerFilterModalComponent implements OnInit {
    @Output() onSuccess = new EventEmitter<boolean>();
    @Input({ required: true }) public assignmentScheduleId!: string;

    private bsModalRef = inject(BsModalRef);
    private formBuilder = inject(FormBuilder);
    private assignmentScheduleService = inject(AssignmentScheduleService);
    private assignmentScheduleStateService = inject(AssignmentScheduleStateService);
    private sharedToasterService = inject(SharedToasterService);
    private translocoService = inject(TranslocoService);

    protected isLoading: boolean = false;
    protected isInitialized: boolean = false;
    protected filterForm!: FormGroup;

    protected levelOptions: SelectOptionModel[] | null = null;
    protected facilitOptions: SelectOptionModel[] | null = null;
    protected mayAnswerOptions: SelectOptionModel[] | null = null;

    constructor(
    ) { }

    ngOnInit(): void {
        const currentFilter = this.assignmentScheduleStateService.assignmentSchedulesFilterSubject.value;
        this.initForm(currentFilter);
        this.setMyAnswerFilterModel();
        this.loadData();
    }

    private initForm(assignmentSchedulesFilter: AssignmentSchedulesFilterModel | null): void {

        this.filterForm = this.formBuilder.group({
            levelIds: [assignmentSchedulesFilter?.levelIds],
            facilityIds: [assignmentSchedulesFilter?.facilityIds],
            myAnswerIds: [assignmentSchedulesFilter?.myAnswerIds],
            weekdays: this.formBuilder.group({
                isMondayActive: [assignmentSchedulesFilter?.weekdays.isMondayActive ?? true],
                isTuesdayActive: [assignmentSchedulesFilter?.weekdays.isTuesdayActive ?? true],
                isWednesdayActive: [assignmentSchedulesFilter?.weekdays.isWednesdayActive ?? true],
                isThursdayActive: [assignmentSchedulesFilter?.weekdays.isThursdayActive ?? true],
                isFridayActive: [assignmentSchedulesFilter?.weekdays.isFridayActive ?? true],
                isSaturdayActive: [assignmentSchedulesFilter?.weekdays.isSaturdayActive ?? true],
                isSundayActive: [assignmentSchedulesFilter?.weekdays.isSundayActive ?? true]
            }),
            isWeekdayFilterActive: [assignmentSchedulesFilter?.isWeekdayFilterActive]
        });
    }

    loadData(): void {
        this.assignmentScheduleService.getAssignmentScheduleFilterOptionsById(this.assignmentScheduleId)
            .subscribe({
                next: (model: AssignmentScheduleFilterOptionsModel) => {
                    this.levelOptions = this.mapAndSortOptions(model.levelOptions);
                    this.facilitOptions = this.mapAndSortOptions(model.facilityOptions);
                },
                error: () => {
                    this.sharedToasterService.showGeneralDataLoadingError();
                }
            });

        this.isInitialized = true;
    }

    private setMyAnswerFilterModel(): void {
        const translate = (key: string) => this.translocoService.translate(`assignmentSchedules.filter.answers.${key}`);

        this.mayAnswerOptions = [
            { id: '0', name: translate('notAnswered'), groupName: '', disabled: false },
            { id: AssignmentScheduleOptionAnswerEnum.yes.toString(), name: translate('yes'), groupName: '', disabled: false },
            { id: AssignmentScheduleOptionAnswerEnum.maybe.toString(), name: translate('maybe'), groupName: '', disabled: false },
            { id: AssignmentScheduleOptionAnswerEnum.no.toString(), name: translate('no'), groupName: '', disabled: false }
        ];
    }

    private mapAndSortOptions(options: AssignmentScheduleFilterOptionModel[]): SelectOptionModel[] {
        if (!options) return [];

        return options
            .map(o => ({
                id: o.id.toString(),
                name: o.name,
                groupName: '',
                disabled: false
            } as SelectOptionModel))
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    protected clear(): void {
        this.initForm(null);
    }

    protected save(): void {
        // this.isSubmitted = true;
        this.filterForm.markAllAsTouched();

        if (!this.filterForm.valid) return;

        const formValue = this.filterForm.value;
        const weekdaysValue = formValue.weekdays;

        const assignmentSchedulesFilter: AssignmentSchedulesFilterModel = {
            assignmentScheduleId: this.assignmentScheduleId,
            levelIds: formValue.levelIds,
            levelNames: this.getOptionName(this.levelOptions, formValue.levelIds),
            facilityIds: formValue.facilityIds,
            facilityNames: this.getOptionName(this.facilitOptions, formValue.facilityIds),
            myAnswerIds: formValue.myAnswerIds,
            myAnswerNames: this.getOptionName(this.mayAnswerOptions, formValue.myAnswerIds),
            weekdays: {
                isMondayActive: weekdaysValue.isMondayActive,
                isTuesdayActive: weekdaysValue.isTuesdayActive,
                isWednesdayActive: weekdaysValue.isWednesdayActive,
                isThursdayActive: weekdaysValue.isThursdayActive,
                isFridayActive: weekdaysValue.isFridayActive,
                isSaturdayActive: weekdaysValue.isSaturdayActive,
                isSundayActive: weekdaysValue.isSundayActive
            },
            isWeekdayFilterActive: formValue.isWeekdayFilterActive
        };

        this.assignmentScheduleStateService.assignmentSchedulesFilterSubject.next(assignmentSchedulesFilter);
        this.close();
    }


    private close(): void {
        this.bsModalRef.hide();
    }

    private getOptionName(options: SelectOptionModel[] | null, ids: string[] | null): string[] {
        if (options == null) return [];
        if (ids == null) return [];

        return options.filter(o => ids.includes(o.id)).map(o => o.name);
    }
}
